import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// File upload controller with drag & drop and direct upload support
// Based on Active Storage guide: https://guides.rubyonrails.org/active_storage_overview.html#direct-uploads
export default class extends Controller {
  static targets = ["dropzone", "input", "preview", "submitButton"]
  static values = { 
    maxSize: Number,
    directUploadUrl: String
  }

  connect() {
    this.setupDragAndDrop()
    this.updateSubmitButton()
  }

  disconnect() {
    this.teardownDragAndDrop()
  }

  setupDragAndDrop() {
    const dropzone = this.hasDropzoneTarget ? this.dropzoneTarget : this.element
    
    // Prevent default drag behaviors
    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.preventDefaults.bind(this), false)
    })

    // Add visual feedback
    ;['dragenter', 'dragover'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.highlight.bind(this), false)
    })

    ;['dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.unhighlight.bind(this), false)
    })

    // Handle drop
    dropzone.addEventListener('drop', this.handleDrop.bind(this), false)
  }

  teardownDragAndDrop() {
    const dropzone = this.hasDropzoneTarget ? this.dropzoneTarget : this.element
    
    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropzone.removeEventListener(eventName, this.preventDefaults.bind(this), false)
    })
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight(e) {
    this.preventDefaults(e)
    const dropzone = this.hasDropzoneTarget ? this.dropzoneTarget : this.element
    dropzone.classList.add('border-theme-accent', 'bg-theme-accent/10')
  }

  unhighlight(e) {
    this.preventDefaults(e)
    const dropzone = this.hasDropzoneTarget ? this.dropzoneTarget : this.element
    dropzone.classList.remove('border-theme-accent', 'bg-theme-accent/10')
  }

  handleDrop(e) {
    this.preventDefaults(e)
    this.unhighlight(e)
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.handleFile(files[0])
    }
  }

  handleFileSelect(e) {
    const files = e.target.files
    if (files.length > 0) {
      this.handleFile(files[0])
    }
  }

  handleFile(file) {
    // Validate file size
    if (this.hasMaxSizeValue && file.size > this.maxSizeValue * 1024 * 1024) {
      alert(`File size exceeds ${this.maxSizeValue}MB limit`)
      return
    }

    // Update the file input
    const dt = new DataTransfer()
    dt.items.add(file)
    this.inputTarget.files = dt.files

    // Show preview immediately
    this.showPreview(file)
    this.updateSubmitButton()

    // Start direct upload if URL is provided
    if (this.hasDirectUploadUrlValue && this.directUploadUrlValue) {
      this.uploadFile(file)
    }
  }

  uploadFile(file) {
    const url = this.directUploadUrlValue || '/rails/active_storage/direct_uploads'
    const upload = new DirectUpload(file, url, this)
    
    // Show uploading status in preview
    this.updatePreviewStatus('uploading')

    upload.create((error, blob) => {
      if (error) {
        console.error('Upload failed:', error)
        alert(`Upload failed: ${error}`)
        this.updatePreviewStatus('error')
      } else {
        // Store the blob signed_id in a hidden field
        this.addHiddenField(blob.signed_id)
        this.updatePreviewStatus('complete')
      }
    })
  }

  // Direct Upload progress tracking
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", event => this.directUploadDidProgress(event))
  }

  directUploadDidProgress(event) {
    const progress = Math.round((event.loaded / event.total) * 100)
    this.updateProgressBar(progress)
  }

  showPreview(file) {
    if (!this.hasPreviewTarget) return

    const previewHTML = `
      <div class="mt-4 p-4 bg-theme-card border border-theme-primary/20 rounded-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div data-file-upload-status-icon>
              <svg class="w-8 h-8 text-theme-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium text-theme-primary">${file.name}</p>
              <p class="text-xs text-theme-muted">${this.formatFileSize(file.size)}</p>
              <div data-file-upload-progress class="hidden mt-2 w-48">
                <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div data-file-upload-progress-bar class="h-full bg-theme-accent transition-all duration-300" style="width: 0%"></div>
                </div>
                <p data-file-upload-progress-text class="text-xs text-theme-muted mt-1">Uploading...</p>
              </div>
            </div>
          </div>
          <button type="button" 
                  class="text-theme-error hover:text-red-400 transition-colors"
                  data-action="click->file-upload#clearFile">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `

    this.previewTarget.innerHTML = previewHTML
    this.previewTarget.classList.remove('hidden')
  }

  updatePreviewStatus(status) {
    const statusIcon = this.element.querySelector('[data-file-upload-status-icon]')
    const progressDiv = this.element.querySelector('[data-file-upload-progress]')
    const progressText = this.element.querySelector('[data-file-upload-progress-text]')
    
    if (!statusIcon) return

    switch(status) {
      case 'uploading':
        if (progressDiv) progressDiv.classList.remove('hidden')
        statusIcon.innerHTML = `
          <svg class="w-8 h-8 text-theme-info animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `
        break
      case 'complete':
        if (progressDiv) progressDiv.classList.add('hidden')
        statusIcon.innerHTML = `
          <svg class="w-8 h-8 text-theme-success" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
        `
        if (progressText) progressText.textContent = 'Upload complete!'
        break
      case 'error':
        if (progressDiv) progressDiv.classList.add('hidden')
        statusIcon.innerHTML = `
          <svg class="w-8 h-8 text-theme-error" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        `
        break
    }
  }

  updateProgressBar(progress) {
    const progressBar = this.element.querySelector('[data-file-upload-progress-bar]')
    const progressText = this.element.querySelector('[data-file-upload-progress-text]')
    
    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
    if (progressText) {
      progressText.textContent = `Uploading... ${progress}%`
    }
  }

  clearFile() {
    this.inputTarget.value = ''
    this.hidePreview()
    this.updateSubmitButton()
    
    // Remove any hidden blob fields
    const hiddenFields = this.element.querySelectorAll('input[name="config_file_blob"]')
    hiddenFields.forEach(field => field.remove())
  }

  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ''
      this.previewTarget.classList.add('hidden')
    }
  }

  updateSubmitButton() {
    // Delegate validation to import-form controller if it exists
    const importFormController = this.application.getControllerForElementAndIdentifier(this.element.closest('form'), 'import-form')
    
    if (importFormController && typeof importFormController.validateForm === 'function') {
      // Let the import-form controller handle validation
      importFormController.validateForm()
    } else {
      // Fallback to simple file validation for other forms
      if (this.hasSubmitButtonTarget) {
        const hasFile = this.inputTarget.files && this.inputTarget.files.length > 0
        this.submitButtonTarget.disabled = !hasFile
        
        if (hasFile) {
          this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
          this.submitButtonTarget.classList.add('hover:bg-theme-accent/80', 'cursor-pointer')
        } else {
          this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
          this.submitButtonTarget.classList.remove('hover:bg-theme-accent/80', 'cursor-pointer')
        }
      }
    }
  }

  addHiddenField(signedId) {
    const hiddenField = document.createElement('input')
    hiddenField.type = 'hidden'
    hiddenField.name = 'config_file_blob'
    hiddenField.value = signedId
    this.element.closest('form').appendChild(hiddenField)
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
}